// src/components/CommissionPaymentScreen.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FiArrowLeft, FiCreditCard, FiAlertCircle, FiCheckCircle,
  FiRefreshCw, FiDollarSign
} from 'react-icons/fi';
import { toast } from 'react-hot-toast';
import supabaseService from '../services/supabaseService';

const CommissionPaymentScreen = () => {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [isPaying, setIsPaying] = useState(false);

  const [providerId, setProviderId] = useState(null);
  const [walletBalance, setWalletBalance] = useState(0);
  const [commission, setCommission] = useState(0);
  const [originalCommission, setOriginalCommission] = useState(0);
  const [paymentStatus, setPaymentStatus] = useState('pending');
  const [monthKey, setMonthKey] = useState(null);

  const [amount, setAmount] = useState('');
  const [amountError, setAmountError] = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);

  // ── تحميل بيانات المزود والعمولة الحالية ────────────────
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const currentUser = supabaseService.currentUser;
      if (!currentUser) {
        toast.error('يجب تسجيل الدخول أولاً');
        navigate('/login');
        return;
      }

      const provider = await supabaseService.getProviderById(currentUser.id);
      if (!provider) {
        toast.error('تعذر تحميل بيانات المزود');
        navigate(-1);
        return;
      }

      setProviderId(provider.id);
      setWalletBalance(provider.wallet || 0);

      const payment = await supabaseService.getCurrentMonthPayment(provider.id);
      if (payment) {
        setCommission(payment.commission || 0);
        setOriginalCommission(payment.commission || 0);
        setPaymentStatus(payment.paymentStatus || 'pending');
        setMonthKey(payment.month);
      } else {
        setCommission(0);
        setOriginalCommission(0);
      }
    } catch (e) {
      console.error('CommissionPaymentScreen load error:', e);
      toast.error('حدث خطأ أثناء تحميل البيانات');
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // ── إعادة تحميل شهر جديد بعد السداد الكامل ──────────────
  const reloadCurrentMonth = async () => {
    setLoading(true);
    try {
      const payment = await supabaseService.getCurrentMonthPayment(providerId);
      if (!payment) {
        toast('لا توجد عمولة مستحقة حالياً', { icon: 'ℹ️' });
        setLoading(false);
        return;
      }

      const isNewMonth =
        monthKey && payment.month &&
        new Date(payment.month).getMonth() !== new Date(monthKey).getMonth();

      if (!isNewMonth && payment.paymentStatus === 'paid') {
        toast('العمولة الحالية مدفوعة بالفعل', { icon: 'ℹ️' });
        setLoading(false);
        return;
      }

      setCommission(payment.commission || 0);
      setOriginalCommission(payment.commission || 0);
      setPaymentStatus(payment.paymentStatus || 'pending');
      setMonthKey(payment.month);
      setAmount('');
      setAmountError(null);

      if (isNewMonth) {
        toast.success('تم تحميل عمولة الشهر الجديد');
      }
    } catch (e) {
      toast.error('حدث خطأ: ' + e.message);
    } finally {
      setLoading(false);
    }
  };

  // ── التحقق من المبلغ ─────────────────────────────────────
  const validateAmount = () => {
    const text = amount.trim();
    if (!text) {
      setAmountError('يرجى إدخال المبلغ');
      return false;
    }
    const value = parseFloat(text);
    if (isNaN(value) || value <= 0) {
      setAmountError('أدخل مبلغاً صحيحاً');
      return false;
    }
    if (value > commission) {
      setAmountError('المبلغ أكبر من العمولة المستحقة');
      return false;
    }
    if (value > walletBalance) {
      setAmountError('رصيد المحفظة غير كافٍ');
      return false;
    }
    setAmountError(null);
    return true;
  };

  const handlePayClick = () => {
    if (!validateAmount()) return;
    setShowConfirm(true);
  };

  // ── تنفيذ الدفع ──────────────────────────────────────────
  const pay = async () => {
    setShowConfirm(false);
    const value = parseFloat(amount.trim());
    setIsPaying(true);

    try {
      const newWallet = walletBalance - value;
      const newCommission = commission - value;
      const isFullyPaid = newCommission <= 0;

      // 1. خصم من المحفظة
      await supabaseService.updateProvider(providerId, {
        wallet: newWallet,
      });

      // 2. تحديث سجل الدفع الشهري
      await supabaseService.updateMonthlyPaymentCommission({
        providerId,
        month: monthKey,
        paidAmount: value,
        isFullyPaid,
      });

      // 3. تسجيل المعاملة
      await supabaseService.recordWalletTransaction({
        userId: providerId,
        userType: 'provider',
        type: 'debit',
        amount: value,
        note: 'دفع العمولة',
      });

      setWalletBalance(newWallet);
      setCommission(isFullyPaid ? 0 : newCommission);
      setPaymentStatus(isFullyPaid ? 'paid' : 'pending');
      setAmount('');

      toast.success('تمت العملية بنجاح ✓');
    } catch (e) {
      console.error('Payment error:', e);
      toast.error('فشل الدفع: ' + e.message);
    } finally {
      setIsPaying(false);
    }
  };

  const setFullAmount = () => {
    const max = Math.min(commission, walletBalance);
    setAmount(max.toFixed(2));
    setAmountError(null);
  };

  const isPaid = commission <= 0;
  const paidSoFar = originalCommission - commission;
  const progress =
    originalCommission > 0
      ? Math.min(1, Math.max(0, paidSoFar / originalCommission))
      : 0;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-gray-900" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" dir="rtl">
      {/* Header */}
      <div className="bg-khidma-black text-white px-4 md:px-6 py-5 shadow-md flex items-center justify-between">
        <button
          onClick={() => navigate(-1)}
          className="text-white hover:text-gray-300 transition-colors p-2 -mr-2 rounded-full hover:bg-white/10"
        >
          <FiArrowLeft className="text-xl md:text-2xl rotate-180" />
        </button>
        <h1 className="text-xl md:text-2xl font-bold">دفع العمولة</h1>
        <div className="w-8 md:w-10" />
      </div>

      <div className="max-w-lg mx-auto p-5 space-y-4">
        {/* بطاقة المحفظة */}
        <div
          className="rounded-2xl p-5 shadow-lg"
          style={{
            background: 'linear-gradient(135deg, #1a1a1a, #2c2c2c)',
          }}
        >
          <div className="flex items-center gap-3">
            <div className="w-13 h-13 w-[52px] h-[52px] rounded-2xl bg-white/10 flex items-center justify-center">
              <FiDollarSign className="text-white" size={24} />
            </div>
            <div className="flex-1">
              <p className="text-white/60 text-xs mb-1">رصيد المحفظة</p>
              <p className="text-white text-2xl font-black">
                {walletBalance.toFixed(2)} د.ج
              </p>
            </div>
            <span
              className={`text-[11px] font-semibold px-2.5 py-1 rounded-lg ${
                walletBalance > 0
                  ? 'bg-green-500/20 text-green-400'
                  : 'bg-white/10 text-white/50'
              }`}
            >
              {walletBalance > 0 ? 'متاح' : 'فارغ'}
            </span>
          </div>
        </div>

        {/* بطاقة العمولة */}
        <div
          className={`rounded-2xl p-5 bg-white border shadow-sm ${
            isPaid ? 'border-green-300' : 'border-gray-200'
          }`}
        >
          <div className="flex items-center gap-3">
            <div
              className={`w-11 h-11 rounded-xl flex items-center justify-center ${
                isPaid ? 'bg-green-100' : 'bg-gray-100'
              }`}
            >
              {isPaid ? (
                <FiCheckCircle className="text-green-600" size={20} />
              ) : (
                <FiCreditCard className="text-gray-700" size={20} />
              )}
            </div>
            <div className="flex-1">
              <p className="text-gray-400 text-xs mb-1">العمولة المستحقة</p>
              <p className="text-gray-900 text-xl font-black">
                {commission.toFixed(2)} د.ج
              </p>
            </div>
            <span
              className={`text-[11px] font-semibold px-2.5 py-1 rounded-lg border ${
                isPaid
                  ? 'bg-green-50 text-green-600 border-green-200'
                  : 'bg-orange-50 text-orange-500 border-orange-200'
              }`}
            >
              {isPaid ? 'مكتمل' : 'معلق'}
            </span>
          </div>

          {originalCommission > 0 && (
            <div className="mt-4">
              <div className="h-2 rounded-full bg-gray-100 overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all ${
                    isPaid ? 'bg-green-500' : 'bg-gray-900'
                  }`}
                  style={{ width: `${progress * 100}%` }}
                />
              </div>
              <div className="flex justify-between mt-2 text-[11px] text-gray-400">
                <span>مكتمل: {paidSoFar.toFixed(2)} د.ج</span>
                <span>الإجمالي: {originalCommission.toFixed(2)} د.ج</span>
              </div>
            </div>
          )}
        </div>

        {/* حقل الإدخال أو شارة الاكتمال */}
        {commission > 0 ? (
          <>
            <div className="bg-white rounded-2xl p-5 border border-gray-200">
              <label className="block text-gray-900 font-bold mb-2 text-sm">
                المبلغ المراد دفعه (دج)
              </label>
              <input
                type="number"
                value={amount}
                onChange={(e) => {
                  setAmount(e.target.value);
                  if (amountError) setAmountError(null);
                }}
                placeholder="0.00"
                className={`w-full text-center text-2xl font-black p-3 border rounded-xl focus:outline-none focus:ring-2 transition ${
                  amountError
                    ? 'border-red-400 focus:ring-red-100'
                    : 'border-gray-300 focus:ring-gray-200 focus:border-gray-900'
                }`}
              />
              {amountError && (
                <p className="text-red-500 text-xs mt-2 flex items-center gap-1">
                  <FiAlertCircle size={12} /> {amountError}
                </p>
              )}

              <button
                onClick={setFullAmount}
                className="w-full mt-4 py-3 rounded-xl border border-gray-200 bg-gray-50 text-gray-900 font-bold text-sm hover:bg-gray-100 transition"
              >
                دفع كامل العمولة
              </button>
            </div>

            <button
              onClick={handlePayClick}
              disabled={isPaying}
              className="w-full bg-khidma-black text-white py-4 rounded-xl font-bold hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-lg flex items-center justify-center gap-2"
            >
              {isPaying ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
                  <span>جاري الدفع...</span>
                </>
              ) : (
                <>
                  <FiDollarSign size={18} />
                  <span>دفع كامل العمولة</span>
                </>
              )}
            </button>
          </>
        ) : (
          <>
            <div className="bg-green-50 border border-green-200 rounded-2xl p-5 flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                <FiCheckCircle className="text-green-600" size={24} />
              </div>
              <div>
                <p className="text-green-700 font-bold text-sm">
                  لا توجد عمولة مستحقة حالياً
                </p>
                <p className="text-green-600/80 text-xs mt-1">
                  تم سداد العمولة بالكامل.
                </p>
              </div>
            </div>

            <button
              onClick={reloadCurrentMonth}
              className="w-full flex items-center justify-center gap-2 py-3.5 rounded-xl border border-gray-200 bg-white text-gray-600 font-semibold text-sm hover:bg-gray-50 transition"
            >
              <FiRefreshCw size={16} />
              تحديث
            </button>
          </>
        )}

        {/* ملاحظة */}
        <div className="bg-gray-100 border border-gray-200 rounded-xl p-3.5 flex items-start gap-2.5">
          <FiAlertCircle className="text-gray-400 mt-0.5 flex-shrink-0" size={16} />
          <p className="text-gray-600 text-xs leading-relaxed">
            يمكنك دفع العمولة على دفعات. المبلغ المدخل يجب أن يكون أقل من أو
            يساوي العمولة المستحقة ورصيد محفظتك.
          </p>
        </div>
      </div>

      {/* Modal تأكيد الدفع */}
      {showConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-sm shadow-xl">
            <h3 className="font-bold text-gray-900 text-lg mb-2">تأكيد الدفع</h3>
            <p className="text-gray-500 text-sm leading-relaxed mb-6">
              سيتم خصم {parseFloat(amount || 0).toFixed(2)} دج من محفظتك.
              <br />
              هل تريد المتابعة؟
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowConfirm(false)}
                className="flex-1 py-3 rounded-xl border border-gray-200 text-gray-600 font-bold text-sm hover:bg-gray-50 transition"
              >
                إلغاء
              </button>
              <button
                onClick={pay}
                className="flex-1 py-3 rounded-xl bg-khidma-black text-white font-bold text-sm hover:bg-gray-800 transition"
              >
                تأكيد
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CommissionPaymentScreen;